/**
 * DeliverOutputStage — one HITL stage per enabled pipeline output.
 *
 * Renders the end-product for a single delivery destination: the mapped export record
 * (field → exportName, with the output's transforms applied) plus any enrichment values
 * inline, so the analyst validates exactly what will be delivered. Approving submits just
 * this output via apiService.submitOutput (API in real time, batch queued for the scheduler);
 * on success the flow advances to the next output. "Back" returns to Validate — never to an
 * already-committed output — so re-review can't double-submit.
 */

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import { apiService } from '../../../services/apiService';
import { OutputStep } from '../../../types';

interface DeliverOutputStageProps {
  output: OutputStep;
  documentId: string;
  /** Current field values from the Validate stage (fields[] + validatedFields{}) plus enrichments. */
  extractedData: any;
  onSubmitted: (outputId: string) => void;
  onBack?: () => void;
}

function applyTransform(value: string, transform?: string): string {
  switch (transform) {
    case 'uppercase':
      return value.toUpperCase();
    case 'lowercase':
      return value.toLowerCase();
    case 'trim':
      return value.trim();
    case 'titlecase':
      return value.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
    default:
      return value;
  }
}

/** Flatten an enrichment object into primitive label/value rows for read-only display. */
function enrichmentRows(enrichments: any): { group: string; rows: { label: string; value: string }[] }[] {
  if (!enrichments || typeof enrichments !== 'object') return [];
  return Object.entries(enrichments).map(([group, payload]) => {
    const rows: { label: string; value: string }[] = [];
    if (payload && typeof payload === 'object') {
      for (const [k, v] of Object.entries(payload as Record<string, unknown>)) {
        if (v == null || typeof v === 'object') continue; // primitives only
        rows.push({ label: k, value: String(v) });
      }
    }
    return { group, rows };
  }).filter((g) => g.rows.length > 0);
}

export default function DeliverOutputStage({
  output,
  documentId,
  extractedData,
  onSubmitted,
  onBack,
}: DeliverOutputStageProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBatch = output.mode === 'batch';

  // Resolve current values by field name (analyst edits from Validate overlay the extracted values).
  const record = useMemo<{ label: string; value: string }[]>(() => {
    const fields: any[] = extractedData?.fields || [];
    const validated: Record<string, any> = extractedData?.validatedFields || {};
    const valuesByName: Record<string, string> = {};
    for (const f of fields) {
      const v = validated[f.id] ?? f.value;
      valuesByName[f.name] = v == null ? '' : String(v);
    }

    const rows: { label: string; value: string }[] = [];
    for (const [fieldName, cfg] of Object.entries(output.fieldMap || {})) {
      if (cfg?.include === false) continue;
      const label = cfg?.exportName || fieldName;
      rows.push({ label, value: applyTransform(valuesByName[fieldName] ?? '', cfg?.transform) });
    }
    return rows;
  }, [extractedData, output.fieldMap]);

  const enrichment = useMemo(() => enrichmentRows(extractedData?.enrichments), [extractedData?.enrichments]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitOutput(documentId, output.id);
      onSubmitted(output.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit output');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Toolbar variant="dense" sx={{ minHeight: 48, borderBottom: 1, borderColor: 'divider', px: 2, gap: 1 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 600, flex: 1 }}>
          {output.label}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={isBatch ? 'default' : 'primary'}
          label={
            isBatch
              ? `Batch ${output.batchFormat ? output.batchFormat.toUpperCase() : ''} → ${output.destinationConnectorId}`.trim()
              : `Real-time API → ${output.destinationConnectorId}`
          }
        />
      </Toolbar>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Delivery record
        </Typography>
        {record.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No fields are mapped for this output. Configure its field mapping in the pipeline settings.
          </Alert>
        ) : (
          <Box sx={{ mb: 2 }}>
            {record.map((row) => (
              <Box
                key={row.label}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
              >
                <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                  {row.value || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {enrichment.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Enrichment
            </Typography>
            {enrichment.map((group) => (
              <Box key={group.group} sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {group.group}
                </Typography>
                {group.rows.map((row) => (
                  <Box
                    key={row.label}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
                  >
                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                      {row.value || '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 2, display: 'flex', gap: 2, flexShrink: 0 }}>
        {onBack && (
          <Button variant="outlined" onClick={onBack} disabled={submitting}>
            ← Back to Validate
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || record.length === 0}
          startIcon={submitting ? <CircularProgress size={16} /> : isBatch ? <ScheduleSendIcon /> : <SendIcon />}
          sx={{ flex: 1 }}
        >
          {submitting
            ? 'Submitting…'
            : isBatch
              ? `Queue for batch export`
              : `Submit to ${output.destinationConnectorId}`}
        </Button>
      </Box>
    </Paper>
  );
}
